import { useEffect, useId, useMemo, useState } from 'react';
import { type Control, useWatch } from 'react-hook-form';

import { Switch } from '@/components/ui/switch';
// Deep import: the clients barrel pulls in route components.
import { useClientPackages } from '@/features/clients/api/use-client-packages';
import { cn } from '@/lib/utils';

import type { AppointmentFieldVariant } from './appointment-create-fields';
import type { AppointmentCreateFormData } from './appointment-create-form';

/**
 * A service the customer still has in a package, offered on the create
 * surfaces (desktop dialog + mobile funnel). Like rooms, it rides alongside the
 * form rather than in it: the shared form contract is untouched, and a customer
 * with no package for the chosen service gets no toggle and an unchanged body.
 */
export interface AppointmentPackageSelection {
  /** The customer's unused package service for the chosen service, if any. */
  match: { clientPackageItemId: string; packageName: string } | null;
  use: boolean;
  setUse: (use: boolean) => void;
  /** Sent with the booking; undefined when nothing applies or staff turned it off. */
  clientPackageItemId: string | undefined;
}

export function useAppointmentPackageSelection(
  control: Control<AppointmentCreateFormData>
): AppointmentPackageSelection {
  const leadId = useWatch({ control, name: 'leadId' }) ?? '';
  const serviceId = useWatch({ control, name: 'serviceId' });
  const { packages } = useClientPackages(leadId);

  const match = useMemo(() => {
    if (!serviceId) return null;
    // The package that runs out first, so a service is never left to expire
    // while the same one in a newer package gets used.
    const usable = packages
      .filter((pkg) => pkg.status === 'active')
      .sort(
        (a, b) =>
          (a.expiresAt ? Date.parse(a.expiresAt) : Number.POSITIVE_INFINITY) -
          (b.expiresAt ? Date.parse(b.expiresAt) : Number.POSITIVE_INFINITY)
      );
    for (const pkg of usable) {
      const item = pkg.items.find(
        (candidate) => candidate.serviceId === serviceId && !candidate.used
      );
      if (item) return { clientPackageItemId: item.id, packageName: pkg.name };
    }
    return null;
  }, [packages, serviceId]);

  // On by default, and again whenever a different package service applies.
  const [use, setUse] = useState(true);
  const matchedItemId = match?.clientPackageItemId;
  useEffect(() => {
    if (matchedItemId) setUse(true);
  }, [matchedItemId]);

  return {
    match,
    use,
    setUse,
    clientPackageItemId: match && use ? match.clientPackageItemId : undefined,
  };
}

/** "Use {package}" for a customer who has the chosen service left in one. */
export function AppointmentPackageField({
  selection,
  variant = 'desktop',
}: {
  selection: AppointmentPackageSelection;
  variant?: AppointmentFieldVariant;
}) {
  const id = useId();
  if (!selection.match) return null;
  const label = `Use ${selection.match.packageName}`;
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4',
        variant === 'mobile'
          ? 'rounded-xl border border-[#E5E5EA] bg-white px-3 py-2.5'
          : 'rounded-xl border border-primary/20 bg-primary/5 p-4'
      )}
    >
      <div className="space-y-0.5">
        <label
          htmlFor={id}
          className={cn(
            'font-medium',
            variant === 'mobile' ? 'text-[15px] text-black' : 'text-sm'
          )}
        >
          {label}
        </label>
        <p
          className={cn(
            variant === 'mobile'
              ? 'text-[13px] text-[#8E8E93]'
              : 'text-sm text-muted-foreground'
          )}
        >
          {selection.use
            ? 'This service is in their package, so the visit is not charged.'
            : 'They pay the usual price, and the service stays in their package.'}
        </p>
      </div>
      <Switch
        id={id}
        aria-label={label}
        checked={selection.use}
        onCheckedChange={selection.setUse}
      />
    </div>
  );
}
